import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import { useForm } from 'react-hook-form';

import { Box, Button, GridLegacy as Grid, TextField } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import BidModificationHistoryTable from '~/components/GoogleAds/BidModificationHistoryTable';

import useAppNavigation from '~/hooks/useAppNavigation';
import useQuery from '~/hooks/useQuery';

import { BidHistoryRecord, getAdGroupBidHistory } from '~/services/PublicOfferFeedService';

interface FormValues {
  customerId?: string;
  campaignId?: string;
  adGroupId?: string;
  search?: string;
}

interface QueryParams extends FormValues {
  page_num: number;
  page_size: number;
}

function BidModificationHistory() {
  const query = useQuery();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<Array<BidHistoryRecord>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pageNum, setPageNum] = useState(Number(query.get('page_num') ?? 0));
  const [pageSize, setPageSize] = useState(Number(query.get('page_size') ?? 100));
  const [totalCount, setTotalCount] = useState(0);
  const { push } = useAppNavigation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormValues>({
    defaultValues: {
      customerId: query.get('customerId') || '',
      campaignId: query.get('campaignId') || '',
      adGroupId: query.get('adGroupId') || '',
      search: query.get('search') || '',
    },
  });

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum,
      page_size: pageSize,
      customerId: getValues('customerId'),
      campaignId: getValues('campaignId'),
      adGroupId: getValues('adGroupId'),
      search: getValues('search'),
    } as QueryParams);

    push({
      search: `?${searchParams}`,
    });
  }, [getValues, pageNum, pageSize, push]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const { result, count } = await getAdGroupBidHistory({
        customerId: getValues('customerId'),
        campaignId: getValues('campaignId'),
        adGroupId: getValues('adGroupId'),
        search: getValues('search'),
        page: pageNum,
        limit: pageSize,
      });

      setData(result);
      setTotalCount(Number(count));
      setQueryParams();
    } catch (error) {
      enqueueSnackbar(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [pageNum, pageSize, getValues, setQueryParams, enqueueSnackbar]);

  const handleSearch = useCallback(async () => {
    fetchData();
  }, [fetchData]);

  const onPageChange = useCallback((page: number, newPageSize: number) => {
    setPageNum(page);
    setPageSize(newPageSize);
  }, []);

  useEffect(() => {
    fetchData();
  }, [pageNum, pageSize, fetchData]);

  return (
    <Box sx={{ m: 3 }}>
      <Helmet>
        <title>Bid Modification History</title>
      </Helmet>

      <PageSubheader title="Search Bid Modification History" />

      <Grid
        container
        spacing={2}
        justifyContent="flex-start"
        alignItems="center"
        component="form"
        onSubmit={handleSubmit(handleSearch)}
      >
        <Grid item>
          <TextField
            variant="outlined"
            label="Customer ID"
            {...register('customerId')}
            error={!!errors.customerId}
            helperText={errors.customerId?.message}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid item>
          <TextField
            variant="outlined"
            label="Campaign ID"
            {...register('campaignId')}
            error={!!errors.campaignId}
            helperText={errors.campaignId?.message}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid item>
          <TextField
            variant="outlined"
            label="Ad Group ID"
            {...register('adGroupId')}
            error={!!errors.adGroupId}
            helperText={errors.adGroupId?.message}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid item>
          <TextField
            variant="outlined"
            label="Search"
            {...register('search')}
            error={!!errors.search}
            helperText={errors.search?.message}
            sx={{ width: 300 }}
          />
        </Grid>

        <Grid item>
          <Button variant="contained" color="primary" type="submit">
            View History
          </Button>
        </Grid>
      </Grid>

      <BidModificationHistoryTable
        rows={data}
        isLoading={isLoading}
        rowCount={totalCount}
        paginationModel={{ page: pageNum, pageSize }}
        onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
      />
    </Box>
  );
}

export default BidModificationHistory;
